import { useEffect, useState } from 'react';
import {
    Loader2, AlertCircle, AlertTriangle, Info, ExternalLink, RotateCw, Check,
} from 'lucide-react';
import { docuwarePoCheckApi } from '../../../services/docuwarePoCheckApi';
import { getBcDocumentUrl, getBcPostedPurchaseUrl } from '../../../utils/bcLinks';
import { formatDate, formatDateTimeCH } from '../../../utils/format';
import type {
    PoCheckDetail as Detail, PoCheckFinding, PoCheckLine, PoReviewStatus,
} from '../../../types/docuwarePoCheck';
import { FINDING_LABELS, REGIME_LABELS, RESOLUTION_LABELS, VAT_SOURCE_LABELS } from '../../../types/docuwarePoCheck';
import { DocuWareDocLink } from '../DocuWareDocLink';

interface Props {
    documentId: number;
    canEdit: boolean;
    onChanged: () => void;
    onToast: (msg: string, kind: 'success' | 'error') => void;
    onCopyDocId: (docId: string) => void;
}

const SEVERITY_STYLE: Record<string, { icon: typeof AlertCircle; cls: string }> = {
    Error: { icon: AlertCircle, cls: 'text-red-600' },
    Warning: { icon: AlertTriangle, cls: 'text-amber-600' },
    Info: { icon: Info, cls: 'text-gray-400' },
};

const REVIEW_OPTIONS: { key: PoReviewStatus; label: string; hint: string }[] = [
    { key: 'Open', label: 'Open', hint: 'Not looked at yet' },
    { key: 'CheckedOk', label: 'Checked, acceptable', hint: 'Looked at; the finding does not need action' },
    { key: 'Disputed', label: 'Disputed', hint: 'Raised with the supplier — a reason is required' },
];

function num(v: number | null | undefined, digits = 2): string {
    return v === null || v === undefined ? '—' : v.toLocaleString('de-CH', {
        minimumFractionDigits: digits, maximumFractionDigits: digits,
    });
}

/** A line whose two sides disagree gets highlighted; equality is left quiet. */
function differs(a: number | null, b: number | null): boolean {
    if (a === null || b === null) return false;
    const scale = Math.max(Math.abs(a), Math.abs(b));
    return Math.abs(a - b) > 0.02 && (scale === 0 || Math.abs(a - b) / scale > 0.005);
}

/**
 * Group findings by their (server-assigned) category, keeping the order they arrive in.
 *
 * No category constants live here on purpose: the label comes with each finding, so this file
 * cannot drift out of step with the checker's own mapping. A finding whose category the server
 * could not assign is not dropped — it lands under "Other", because a finding nobody sees is
 * strictly worse than one under an imperfect heading.
 */
function groupFindingsByCategory(findings: PoCheckFinding[]) {
    const groups: { key: string; label: string; findings: PoCheckFinding[] }[] = [];
    for (const f of findings) {
        const key = f.category ?? 'other';
        const label = f.categoryLabel ?? 'Other';
        const existing = groups.find(g => g.key === key);
        if (existing) existing.findings.push(f);
        else groups.push({ key, label, findings: [f] });
    }
    return groups;
}

function FindingRow({ f }: { f: PoCheckFinding }) {
    const style = SEVERITY_STYLE[f.severity] ?? SEVERITY_STYLE.Info;
    const Icon = style.icon;
    const meta = FINDING_LABELS[f.code];
    return (
        <li className="flex gap-2.5 py-2">
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.cls}`} />
            <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-sm font-medium text-gray-900">{meta?.label ?? f.code}</span>
                    {f.lineRef && <span className="text-xs text-gray-500">{f.lineRef}</span>}
                    <span className="font-mono text-[10px] uppercase tracking-wide text-gray-400">{f.code}</span>
                </div>
                <p className="text-sm text-gray-700">{f.message}</p>
                {(f.expected || f.actual) && (
                    <div className="mt-1 grid gap-x-6 gap-y-0.5 text-xs sm:grid-cols-2">
                        {f.expected && (
                            <div><span className="text-gray-400">Order says </span>
                                <span className="text-gray-800">{f.expected}</span></div>
                        )}
                        {f.actual && (
                            <div><span className="text-gray-400">Invoice says </span>
                                <span className="text-gray-800">{f.actual}</span></div>
                        )}
                    </div>
                )}
                {meta?.explain && <p className="mt-1 text-xs italic text-gray-500">{meta.explain}</p>}
            </div>
        </li>
    );
}

/**
 * The side-by-side. Order on the left, invoice on the right, one row per pair.
 *
 * `matchBasis` is shown rather than hidden: a pair found only because two descriptions look
 * alike is a weaker statement than one confirmed by quantity and price, and a reviewer
 * deciding whether to believe a price finding needs to know which they are looking at.
 */
function LineTable({ lines }: { lines: PoCheckLine[] }) {
    if (lines.length === 0) return null;
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-xs">
                <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-500">
                        <th className="py-1.5 pr-2 font-medium">Matched on</th>
                        <th className="py-1.5 pr-2 font-medium">Order line</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Qty</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Price</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Amount</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Received</th>
                        <th className="py-1.5 pr-4 font-medium">VAT grp</th>
                        <th className="py-1.5 pr-2 font-medium">Invoice line</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Qty</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Price</th>
                        <th className="py-1.5 pr-2 text-right font-medium">Amount</th>
                        <th className="py-1.5 font-medium">VAT code</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map(l => {
                        const qtyOff = differs(l.poQuantity, l.invQuantity);
                        const priceOff = differs(l.poUnitCost, l.invUnitPrice);
                        const amtOff = differs(l.poLineAmount, l.invLineAmount);
                        const onlyOneSide = !l.poLineNo || !l.invLineNo;
                        return (
                            <tr key={l.sortOrder}
                                className={`border-b border-gray-100 align-top ${onlyOneSide ? 'bg-amber-50/40' : ''}`}>
                                <td className="py-1.5 pr-2 text-gray-400">{l.matchBasis}</td>
                                <td className="py-1.5 pr-2">
                                    {l.poLineNo ? (
                                        <><span className="font-mono text-gray-500">{l.poItemNo}</span>
                                            <div className="text-gray-700">{l.poDescription}</div></>
                                    ) : <span className="text-gray-400">—</span>}
                                </td>
                                <td className={`py-1.5 pr-2 text-right ${qtyOff ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                                    {num(l.poQuantity, 3)} <span className="text-gray-400">{l.poUom}</span>
                                </td>
                                <td className={`py-1.5 pr-2 text-right ${priceOff ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                                    {num(l.poUnitCost, 4)}
                                </td>
                                <td className={`py-1.5 pr-2 text-right ${amtOff ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                                    {num(l.poLineAmount)}
                                </td>
                                <td className="py-1.5 pr-2 text-right text-gray-500">{num(l.poQtyReceived, 3)}</td>
                                <td className="py-1.5 pr-4 font-mono text-gray-500">{l.poVatProdGroup}</td>
                                <td className="py-1.5 pr-2">
                                    {l.invLineNo ? (
                                        <><span className="font-mono text-gray-500">{l.invItemNo}</span>
                                            <div className="text-gray-700">{l.invDescription}</div></>
                                    ) : <span className="text-gray-400">—</span>}
                                </td>
                                <td className={`py-1.5 pr-2 text-right ${qtyOff ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                                    {num(l.invQuantity, 3)} <span className="text-gray-400">{l.invUom}</span>
                                </td>
                                <td className={`py-1.5 pr-2 text-right ${priceOff ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                                    {num(l.invUnitPrice, 4)}
                                </td>
                                <td className={`py-1.5 pr-2 text-right ${amtOff ? 'font-semibold text-red-600' : 'text-gray-700'}`}>
                                    {num(l.invLineAmount)}
                                </td>
                                <td className="py-1.5 font-mono text-gray-500">{l.invVatCode}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export function PoCheckDetailPanel({ documentId, canEdit, onChanged, onToast, onCopyDocId }: Props) {
    const [detail, setDetail] = useState<Detail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [comment, setComment] = useState('');

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const d = await docuwarePoCheckApi.detail(documentId);
            setDetail(d);
            setComment(d.check.reviewComment ?? '');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load the check');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [documentId]);

    const rerun = async (force: boolean) => {
        setBusy(true);
        try {
            await docuwarePoCheckApi.run(documentId, force);
            onToast(force ? 'Document re-read and re-checked' : 'Re-checked', 'success');
            await load();
            onChanged();
        } catch (err) {
            onToast(err instanceof Error ? err.message : 'Re-check failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const saveReview = async (status: PoReviewStatus) => {
        if (status === 'Disputed' && comment.trim().length === 0) {
            onToast('A reason is required when disputing a check', 'error');
            return;
        }
        setBusy(true);
        try {
            await docuwarePoCheckApi.review(documentId, status, comment);
            onToast('Review saved', 'success');
            await load();
            onChanged();
        } catch (err) {
            onToast(err instanceof Error ? err.message : 'Could not save the review', 'error');
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
        </div>;
    }
    if (error || !detail) {
        return <div className="px-4 py-3 text-sm text-red-700">{error ?? 'Not found'}</div>;
    }

    const c = detail.check;
    // BC page 50 holds OPEN purchase orders only. Once an order is fully posted it is gone
    // from that page, so linking a 'posted' order there opens an empty card — the same
    // failure the BC document link had. Link only where the record can still be found.
    const poIsOpen = c.poResolution === 'open' || c.poResolution === 'multi';
    const poUrl = c.poNo && poIsOpen ? getBcDocumentUrl(c.companyCode, c.poNo, 'purchaseOrder') : null;
    const bcUrl = getBcPostedPurchaseUrl(c.companyCode, c.bcDocumentNo, c.bcDocumentKind);

    return (
        <div className="space-y-4 bg-gray-50 px-4 py-4">
            {/* What the order says against what the document says, side by side. This block
                is the whole argument of the tab: the numbers can agree completely and the
                two sides still describe different transactions. */}
            <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Purchase order
                    </h4>
                    <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                        <dt className="text-gray-500">Order</dt>
                        <dd className="text-gray-900">
                            {poUrl ? (
                                <a href={poUrl} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                                    {c.poNo}<ExternalLink className="h-3 w-3" />
                                </a>
                            ) : (
                                <span title={c.poNo && !poIsOpen
                                    ? 'The order is fully posted — Business Central has no open order card for it.'
                                    : undefined}>
                                    {c.poNo ?? '—'}
                                </span>
                            )}
                            <span className="ml-2 text-xs text-gray-400">
                                ({RESOLUTION_LABELS[c.poResolution]})
                            </span>
                        </dd>
                        <dt className="text-gray-500">Expected VAT</dt>
                        <dd className="text-gray-900">
                            {c.expectedRegime ? (REGIME_LABELS[c.expectedRegime] ?? c.expectedRegime) : '—'}
                            {/* Provenance, not decoration: a finding measured against the
                                posted VAT entry is a weaker claim than one measured against
                                the order, and the reviewer decides what to do with it. */}
                            {c.vatSource && (
                                <span
                                    className="ml-2 text-xs text-gray-400"
                                    title={c.vatSource === 'posting'
                                        ? 'The order is no longer in Business Central, so the VAT treatment was read from the posted VAT entry.'
                                        : 'The VAT treatment was read from the purchase order itself.'}
                                >
                                    ({VAT_SOURCE_LABELS[c.vatSource] ?? c.vatSource})
                                </span>
                            )}
                        </dd>
                        <dt className="text-gray-500">Posting</dt>
                        <dd className="text-gray-900">
                            {bcUrl ? (
                                <a href={bcUrl} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                                    {c.bcDocumentNo}<ExternalLink className="h-3 w-3" />
                                </a>
                            ) : (c.bcDocumentNo ?? 'not posted')}
                            {c.bcPostingDate && (
                                <span className="ml-2 text-xs text-gray-400">{formatDate(c.bcPostingDate)}</span>
                            )}
                            {c.bcDocumentKind === 'ledger' && (
                                <span className="ml-2 text-xs text-amber-700">journal posting</span>
                            )}
                            {/* A collective invoice, said out loud. The number above is the
                                FIRST posting by convention, and it is also the reason this
                                check compared against the purchase order instead of the booked
                                invoice (po-check/run.ts, the collective guard). Showing only
                                the first number would leave that verdict unexplained. */}
                            {c.bcPostings && c.bcPostings !== c.bcDocumentNo && (
                                <div className="mt-1 text-xs text-amber-700">
                                    Posted as several documents:{' '}
                                    <span className="font-mono">{c.bcPostings}</span> — the
                                    comparison therefore runs against the purchase order, not
                                    against one part of the invoice.
                                </div>
                            )}
                        </dd>
                    </dl>
                </div>

                <div className="rounded border border-gray-200 bg-white p-3">
                    {/* The way to the document itself. Every claim in this panel is a reading
                        of that PDF, so it has to be one click away from the reading. */}
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Invoice as read
                        </h4>
                        <DocuWareDocLink row={c} onCopyDocId={onCopyDocId} variant="inline" />
                    </div>
                    <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                        <dt className="text-gray-500">Claimed VAT</dt>
                        <dd className="text-gray-900">
                            {c.actualRegime ? (REGIME_LABELS[c.actualRegime] ?? c.actualRegime) : '—'}
                            {c.actualCounterpartyBloc && (
                                <span className="ml-2 text-xs text-gray-400">
                                    customer treated as {c.actualCounterpartyBloc === 'EU' ? 'inside the EU' : 'outside the EU'}
                                </span>
                            )}
                        </dd>
                        <dt className="text-gray-500">Read by</dt>
                        <dd className="text-gray-700">
                            {c.extractionStatus === 'ok'
                                ? <>{c.extractionModel}{c.extractedAt && <span className="ml-2 text-xs text-gray-400">{formatDateTimeCH(c.extractedAt)}</span>}</>
                                : <span className="text-amber-700">{c.extractionStatus}{c.extractionError ? ` — ${c.extractionError}` : ''}</span>}
                        </dd>
                        <dt className="text-gray-500">Checked</dt>
                        <dd className="text-gray-700">{c.checkedAt ? formatDateTimeCH(c.checkedAt) : '—'}</dd>
                    </dl>
                </div>
            </div>

            {detail.findings.length > 0 && (
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Findings
                    </h4>
                    {/* Grouped by category, in order of FIRST APPEARANCE — the server sorts
                        errors first, so the category holding the worst problem leads. A fixed
                        category order would bury a single error under four warnings. */}
                    {groupFindingsByCategory(detail.findings).map(g => (
                        <div key={g.key} className="mt-2 first:mt-0">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {g.label}
                            </div>
                            <ul className="divide-y divide-gray-100">
                                {g.findings.map(f => <FindingRow key={f.id} f={f} />)}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {detail.lines.length > 0 && (
                <div className="rounded border border-gray-200 bg-white p-3">
                    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Order lines against invoice lines
                    </h4>
                    <LineTable lines={detail.lines} />
                </div>
            )}

            <div className="rounded border border-gray-200 bg-white p-3">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Review
                </h4>
                {c.reviewedBy && (
                    <p className="mb-2 text-xs text-gray-500">
                        Last set by {c.reviewedBy}
                        {c.reviewedAt && ` on ${formatDateTimeCH(c.reviewedAt)}`}
                    </p>
                )}
                <textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    disabled={!canEdit || busy}
                    rows={2}
                    maxLength={1000}
                    placeholder="What did you find out? Required when disputing."
                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50"
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    {REVIEW_OPTIONS.map(o => (
                        <button
                            key={o.key}
                            onClick={() => void saveReview(o.key)}
                            disabled={!canEdit || busy}
                            title={o.hint}
                            className={`inline-flex h-8 items-center gap-1.5 rounded border px-3 text-sm disabled:opacity-40 ${
                                c.reviewStatus === o.key
                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            {c.reviewStatus === o.key && <Check className="h-3.5 w-3.5" />}
                            {o.label}
                        </button>
                    ))}
                    <span className="grow" />
                    {/* Two buttons, because they cost very different things. "Re-check" just
                        re-runs the rules against what we already read; "Re-read" pays for the
                        document to be transcribed again. */}
                    <button
                        onClick={() => void rerun(false)}
                        disabled={!canEdit || busy}
                        title="Re-run the rules against the stored reading of the document"
                        className="inline-flex h-8 items-center gap-1.5 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                        <RotateCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Re-check
                    </button>
                    <button
                        onClick={() => void rerun(true)}
                        disabled={!canEdit || busy}
                        title="Read the PDF again from DocuWare, then re-run the rules. Takes about 20 seconds."
                        className="inline-flex h-8 items-center gap-1.5 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                        Re-read document
                    </button>
                </div>
                {!canEdit && (
                    <p className="mt-2 text-xs text-gray-400">
                        You have read access to this page; recording a review needs edit rights.
                    </p>
                )}
            </div>
        </div>
    );
}
